import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

import {
  getTabOrganizationId,
  ORGANIZATION_HEADER,
} from '@/lib/current-organization';
import { env } from '@/lib/env';
import { i18n } from '@/lib/i18n/i18n';
import { resetIdentity } from '@/lib/identity';
import {
  leaveIfOrganizationUnavailable,
  reloadIfOrganizationRequired,
} from '@/lib/organization-unavailable';

export const http = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// The API words its refusals in this language before it knows who is asking, like a rejected upload or a login rate limit.
http.interceptors.request.use((config) => {
  config.headers.set('Accept-Language', i18n.language);
  const organizationId = getTabOrganizationId();
  if (organizationId !== null) {
    config.headers.set(ORGANIZATION_HEADER, String(organizationId));
  }
  return config;
});

axiosRetry(http, {
  retries: 2,
  retryDelay: (retryCount, error) =>
    axiosRetry.exponentialDelay(retryCount, error),
  retryCondition: (error) =>
    axiosRetry.isNetworkOrIdempotentRequestError(error) ||
    error.response?.status === 503,
});

// --- Refresh interceptor ---
// Serializes concurrent 401s: only one refresh runs at a time; others queue behind it.
type RetryableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

let isRefreshing = false;
// Sticky flag: once /auth/refresh has failed in this tab, every subsequent
// 401 short-circuits the refresh path. Cleared in the success interceptor on
// /auth/login or /auth/refresh — so a real login event re-arms refresh, but
// a redirect-loop on an unauthenticated /login page can't keep hitting the
// rate limiter.
let refreshAttemptFailed = false;
let pendingQueue: Array<{ resolve: () => void; reject: (e: unknown) => void }> =
  [];

const drainQueue = (error?: unknown): void => {
  for (const p of pendingQueue) {
    if (error) p.reject(error);
    else p.resolve();
  }
  pendingQueue = [];
};

const waitForRefresh = (): Promise<void> =>
  new Promise((resolve, reject) => pendingQueue.push({ resolve, reject }));

const toError = (e: unknown): Error =>
  e instanceof Error ? e : new Error(String(e));

const isEligibleForRetry = (
  error: AxiosError,
  config: RetryableConfig | undefined,
): config is RetryableConfig => {
  const is401 = error.response?.status === 401;
  // A 401 from either of these is never a stale session a refresh could fix: a login 401 is
  // wrong credentials, and a refresh 401 is what refreshAndRetry itself just tried.
  const isAuthEndpointCall =
    config?.url?.includes('/auth/refresh') ||
    config?.url?.includes('/auth/login');
  const alreadyRetried = config?._authRetried;
  return Boolean(is401 && config && !isAuthEndpointCall && !alreadyRetried);
};

const queueRetry = async (config: RetryableConfig): Promise<unknown> => {
  await waitForRefresh();
  config._authRetried = true;
  return http(config);
};

const refreshAndRetry = async (config: RetryableConfig): Promise<unknown> => {
  isRefreshing = true;
  config._authRetried = true;
  try {
    await http.post('/api/v1/auth/refresh');
    drainQueue();
    return await http(config);
  } catch (refreshError) {
    refreshAttemptFailed = true;
    drainQueue(refreshError);
    // Local only — signalling other tabs reloads them into their own 401, which re-signals this one.
    resetIdentity();
    // No redirect either: a reload resets module state and re-arms the loop.
    throw toError(refreshError);
  } finally {
    isRefreshing = false;
  }
};

http.interceptors.response.use(
  (response) => {
    const url = response.config?.url ?? '';
    if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
      refreshAttemptFailed = false;
    }
    return response;
  },
  async (error: unknown) => {
    const axiosError = error as AxiosError;
    const config = axiosError.config as RetryableConfig | undefined;

    const left =
      leaveIfOrganizationUnavailable(
        Boolean(config?.headers.has(ORGANIZATION_HEADER)),
        axiosError.response?.status,
        axiosError.response?.data,
      ) ||
      reloadIfOrganizationRequired(
        axiosError.response?.status,
        axiosError.response?.data,
      );
    // Never settles: a caller reading this 404 as "gone" would act on it before the reload lands.
    if (left) return new Promise<never>(() => {});

    if (!isEligibleForRetry(axiosError, config) || refreshAttemptFailed) {
      return Promise.reject(toError(error));
    }

    if (isRefreshing) {
      try {
        return await queueRetry(config);
      } catch {
        return Promise.reject(toError(error));
      }
    }

    return refreshAndRetry(config);
  },
);
